import { useState } from 'react';
import { useUpdate, useList } from '@refinedev/core';
import { FormStickyActions, FormSpacer } from '@/components/forms/form-sticky-actions';
import {
  ScheduleEditor,
  ScheduleNotificationSettings,
  type ScheduleDay,
  type ScheduleNotificationSettingsValue,
} from '@/components/schedule';
import type { Language } from '@/components/translations';
import type { Course } from '../types';

interface LanguageRecord {
  code: string;
  name: string;
  nativeName: string;
  flag?: string | null;
  flagEmoji?: string | null;
}

interface CourseScheduleTabProps {
  course: Course;
  onUpdated: () => void;
}

export function CourseScheduleTab({ course, onUpdated }: CourseScheduleTabProps) {
  const { mutateAsync: update, mutation } = useUpdate();

  const { result: langResult } = useList<LanguageRecord>({ resource: 'languages' });
  const languages: Language[] = (langResult?.data ?? []).map((l) => ({
    code: l.code,
    name: l.name,
    nativeName: l.nativeName,
    flagEmoji: l.flag ?? l.flagEmoji ?? '🏳️',
  }));

  const [activeLang, setActiveLang] = useState('ru');

  const [schedule, setSchedule] = useState<ScheduleDay[]>(
    course.schedule ?? [],
  );
  const [timezone, setTimezone] = useState(
    course.scheduleTimezone ?? 'Asia/Tashkent',
  );
  const [notificationSettings, setNotificationSettings] =
    useState<ScheduleNotificationSettingsValue | null>(
      (course.scheduleNotificationSettings as ScheduleNotificationSettingsValue | null) ?? null,
    );

  const handleSave = async () => {
    await update({
      resource: 'courses',
      id: course.id,
      values: {
        schedule,
        scheduleTimezone: timezone,
        scheduleNotificationSettings: notificationSettings,
      },
    });
    onUpdated();
  };

  const isPending = mutation.isPending;

  return (
    <div className="space-y-6 pt-6">
      <ScheduleEditor
        value={schedule}
        onChange={setSchedule}
        minDate={course.startDate ? new Date(course.startDate) : undefined}
        maxDate={course.endDate ? new Date(course.endDate) : undefined}
        languages={languages}
        activeLanguage={activeLang}
        onLanguageChange={setActiveLang}
      />

      <ScheduleNotificationSettings
        value={notificationSettings}
        onChange={setNotificationSettings}
        timezone={timezone}
        onTimezoneChange={setTimezone}
        languages={languages}
        activeLanguage={activeLang}
        onLanguageChange={setActiveLang}
      />

      <FormSpacer />
      <FormStickyActions
        primaryLabel="Сохранить"
        pendingLabel="Сохранение..."
        onPrimary={handleSave}
        isPending={isPending}
      />
    </div>
  );
}
